import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Tag,
  Space,
  MessagePlugin
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, BrowseIcon, CheckCircleIcon, CloseCircleIcon } from 'tdesign-icons-react';
import { articleApi } from '../../api';
import type { ArticleVO } from '@/types/api-entity';
import RichTextEditor from '../../components/RichTextEditor';
import CoverImageUploader from '../../components/CoverImageUploader';

// ===================== Token 工具 =====================
const USER_TYPE_KEY = 'userType';
const getUserType = (): string | null => localStorage.getItem(USER_TYPE_KEY);

// ===================== 文章管理 =====================
const statusOptions = [
  { label: '草稿', value: 0 },
  { label: '待审核', value: 1 },
  { label: '已发布', value: 2 },
  { label: '不通过', value: 3 },
  { label: '已下线', value: 4 },
];

export default function ArticleManage() {
  const [data, setData] = useState<ArticleVO[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  
  // ===================== 预览相关状态 =====================
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewArticle, setPreviewArticle] = useState<ArticleVO | null>(null);

  // ===================== 编辑相关状态 =====================
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingArticle, setEditingArticle] = useState<ArticleVO | null>(null);
  
  // ===================== 审核相关状态 =====================
  const [checkVisible, setCheckVisible] = useState(false);
  const [checkingArticle, setCheckingArticle] = useState<ArticleVO | null>(null);

  const [content, setContent] = useState('');
  const [mainImageUrl, setMainImageUrl] = useState('');
  const formRef = useRef<any>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'content'>('basic');
  
  const [checkForm] = Form.useForm();
  // 判断是否为管理用户（userType === 1），从 adminInfo 读取
  const [isAdmin] = useState(() => {
    try {
      const info = localStorage.getItem('adminInfo');
      if (info) {
        const parsed = JSON.parse(info);
        return parsed.userType === 1;
      }
    } catch {
      // ignore
    }
    return false;
  });

  const columns = [
    { colKey: 'id', title: 'ID', width: 80 },
    { colKey: 'title', title: '标题', ellipsis: true },
    { colKey: 'category', title: '分类', width: 120 },
    { colKey: 'authorName', title: '作者', width: 120 },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => {
        const statusMap: Record<number, { label: string; theme: string }> = {
          0: { label: '草稿', theme: 'default' },
          1: { label: '待审核', theme: 'primary' },
          2: { label: '已发布', theme: 'success' },
          3: { label: '不通过', theme: 'warning' },
          4: { label: '已下线', theme: 'danger' },
        };
        const s = statusMap[row.status] || { label: '未知', theme: 'default' };
        return <Tag theme={s.theme as any}>{s.label}</Tag>;
      },
    },
    { colKey: 'viewCount', title: '浏览量', width: 100 },
    {
      colKey: 'isTop',
      title: '置顶',
      width: 80,
      cell: ({ row }: any) =>
        row.isTop === 1 ? <Tag theme="primary">是</Tag> : <Tag theme="default">否</Tag>,
    },
    { colKey: 'createTime', title: '创建时间', width: 180 },
    { colKey: 'publishTime', title: '发布时间', width: 180 },
    { colKey: 'offlineTime', title: '下线时间', width: 180 },
    { colKey: 'updateTime', title: '最后更新时间', width: 180 },
    {
      colKey: 'action',
      title: '操作',
      width: 420,
      cell: ({ row }: any) => (
        <Space>
          <Button theme="primary" variant="text" icon={<BrowseIcon />} onClick={() => handlePreview(row)} loading={detailLoading}>预览</Button>
          <Button theme="success" variant="text" icon={<EditIcon />} onClick={() => handleEdit(row)} loading={detailLoading}>编辑</Button>
          {/* 只有管理员(userType=1)且状态为待审核(1)时才显示审核按钮 */}
          {isAdmin && row.status === 1 && (
            <Button theme="warning" variant="text" icon={<CheckCircleIcon />} onClick={() => handleCheck(row)} loading={detailLoading}>审核</Button>
          )}
          <Popconfirm content="确定删除该文章？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const res = await articleApi.getAdminArticleList({ pageNum: page, pageSize });
      if (res.code === 200) {
        setData(res.data.list);
        setPagination({ current: res.data.pageNum, pageSize: res.data.pageSize, total: res.data.total });
      }
    } catch {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  // ===================== 修改点 1: 增加一个重置表单状态的辅助函数 =====================
const resetFormState = () => {
  setContent('');
  setMainImageUrl('');
  setActiveTab('basic');
  setEditingArticle(null);
  // 如果 formRef 有 reset 方法，也可以调用
  if (formRef.current) {
    formRef.current.reset();
  }
};
  const handleAdd = () => {
    resetFormState()
    setDialogVisible(true);
  };
  // ======================预览
  const handlePreview = async (article: ArticleVO) => {
    setDetailLoading(true);
    try {
      const res = await articleApi.getAdminArticleDetail(article.id);
      if (res.code !== 200 || !res.data) {
        MessagePlugin.error(res.message || '文章详情为空');
        return;
      }
      setPreviewArticle(res.data);
      setPreviewVisible(true);
    } catch (err) {
      MessagePlugin.error('加载预览内容失败');
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };
  
  // ======================编辑
  const handleEdit = async (article: ArticleVO) => {
    setDetailLoading(true);
    try {
      const res = await articleApi.getAdminArticleDetail(article.id);
      if (res.code !== 200 || !res.data) {
        MessagePlugin.error(res.message || '文章详情为空');
        return;
      }
      const detail = res.data;
      
      // 先设置数据，再打开弹窗
      setEditingArticle(detail);
      setContent(detail.content || '');
      setMainImageUrl(detail.coverImage || '');
      setActiveTab('basic');
      
      // 注意：TDesign Form 的 initialData 通常在组件挂载时生效。
      // 如果是复用组件，可能需要手动设置表单值，或者依靠 key 强制刷新。
      // 这里我们依靠下面的 Dialog key 机制来保证初始值正确。
      
      setDialogVisible(true);
    } catch (err) {
      MessagePlugin.error('加载文章详情失败');
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  // ======================新增、编辑保存
  const handleSubmit = async () => {
    try {
      // 【修复】通过 formRef 获取实例并验证
      const result = await formRef.current?.validate();
      
      // validate 返回 true 表示成功，或者包含错误信息
      if (result !== true) {
        return;
      }
       // 获取 Form 中的标准字段值
      // TDesign Form getFieldsValue 获取当前表单所有字段的值
      const values = formRef.current?.getFieldsValue(true) || {};
      const submitData = {
        ...values,
        content,
        coverImage: mainImageUrl,
        status: values.status ?? 0,
        isTop: values.isTop ? 1 : 0,
        sortOrder: values.sortOrder ?? 0,
      };
      const res = editingArticle
        ? await articleApi.updateArticle(editingArticle.id, submitData)
        : await articleApi.createArticle(submitData);
      if (res.code === 200) {
        MessagePlugin.success(editingArticle ? '更新成功' : '创建成功');
        setDialogVisible(false);
        setEditingArticle(null);
        fetchData();
      } else {
        MessagePlugin.error(res.message || '操作失败');
      }
    } catch (err) {
      console.error('提交失败：', err);
      MessagePlugin.error('表单验证失败或服务器异常');
    }
  };

  // ======================删除
  const handleDelete = async (id: number) => {
    try {
      const res = await articleApi.deleteArticle(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        fetchData();
      } else {
        MessagePlugin.error(res.message || '删除失败');
      }
    } catch {
      MessagePlugin.error('删除失败');
    }
  };
  
  // ======================审核
  const handleCheck = async (article: ArticleVO) => {
    setDetailLoading(true);
    try {
      const res = await articleApi.getAdminArticleDetail(article.id);
      if (res.code !== 200 || !res.data) {
        MessagePlugin.error(res.message || '文章详情为空');
        return;
      }
      
      const detail = res.data;
      
      // 1. 先设置数据状态
      setCheckingArticle(detail);
      setContent(detail.content || '');
      setMainImageUrl(detail.coverImage || '');
      
      // 2. 打开弹窗（让表单组件挂载）
      setCheckVisible(true);
      
      // 3. 使用 setTimeout 等待 Dialog 渲染完成后再设置表单值
      setTimeout(() => {
        checkForm.setFieldsValue({
          title: detail.title,
          summary: detail.summary || '',
          category: detail.category || '',
          coverImage: detail.coverImage || '',
          tags: detail.tags || '',
          status: detail.status,
          isTop: detail.isTop === 1,
          sortOrder: detail.sortOrder,
        });
      }, 100);
      
    } catch (err) {
      MessagePlugin.error('加载文章详情失败');
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  // ======================提交审核
  const handleCheckSubmit = async (checkStatus: number) => {
    if (!checkingArticle) return;
    
    try {
      const res = await articleApi.checkArticleStatus(checkingArticle.id, checkStatus);
      if (res.code === 200) {
        MessagePlugin.success(checkStatus === 2 ? '审核不通过' : '审核通过');
        setCheckVisible(false);
        setCheckingArticle(null);
        fetchData(); // 刷新列表
      } else {
        MessagePlugin.error(res.message || '审核失败');
      }
    } catch (err) {
      console.error('审核失败:', err);
      MessagePlugin.error('审核失败，请稍后重试');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">资讯中心</h1>
        <Button theme="primary" icon={<AddIcon />} onClick={handleAdd}>新增文章</Button>
      </div>

      <Table
        data={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ ...pagination, onChange: (pageInfo) => fetchData(pageInfo.current, pageInfo.pageSize) }}
      />

      {/* 新增/编辑弹窗 */}
      <Dialog
        header={editingArticle ? '编辑文章' : '新增文章'}
        visible={dialogVisible}
        onClose={() => {
          setDialogVisible(false)
          resetFormState();
        }}
        onConfirm={handleSubmit}
        width={900}
        // 【关键修改】添加 key，确保每次打开都是全新的表单实例，避免旧数据残留
        key={editingArticle ? `edit-${editingArticle.id}` : 'add-new'}
      >
        <div className="mb-4 border-b">
          <div className="flex">
            <button
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'basic' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('basic')}
            >
              基本信息
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'content' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('content')}
            >
              文章内容
            </button>
          </div>
        </div>

        <div style={{ display: activeTab === 'basic' ? 'block' : 'none' }}>
          <Form 
            ref={formRef}
            initialData={editingArticle || {}}  
            labelWidth={100}>
            <Form.FormItem label="标题" name="title" rules={[{ required: true, message: '请输入标题' }]}>
              <Input placeholder="文章标题" />
            </Form.FormItem>
            <Form.FormItem label="摘要" name="summary">
              <Input placeholder="文章摘要" />
            </Form.FormItem>
            <Form.FormItem label="分类" name="category">
              <Input placeholder="分类" />
            </Form.FormItem>
            <Form.FormItem label="封面图" name="coverImage">
              <CoverImageUploader 
                value={mainImageUrl} 
                onChange={(val) => {
                  setMainImageUrl(Array.isArray(val) ? val[0] : val);
                }}
                simple 
              />
            </Form.FormItem>
            <Form.FormItem label="标签" name="tags">
              <Input placeholder="逗号分隔" />
            </Form.FormItem>
            {isAdmin && (
              <>
                <Form.FormItem label="状态" name="status" initialValue={0}>
                  <Select options={statusOptions} />
                </Form.FormItem>
                <Form.FormItem label="置顶" name="isTop" initialValue={false}>
                  <Switch />
                </Form.FormItem>
                <Form.FormItem label="排序" name="sortOrder" initialValue={0}>
                  <Input type="number" placeholder="排序值" />
                </Form.FormItem>
              </>
            )}
          </Form>
        </div>

        <div style={{ display: activeTab === 'content' ? 'block' : 'none' }}>
          <RichTextEditor value={content} onChange={setContent} />
        </div>
      </Dialog>

      {/* 审核弹窗 */}
      <Dialog
        header="文章审核"
        visible={checkVisible}
        onClose={() => {
          setCheckVisible(false);
          setCheckingArticle(null);
          checkForm.reset();
        }}
        footer={null}
        width={900}
      >
        {checkingArticle && (
          <div>
            {/* 提示信息 */}
            <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>提示：</strong> 审核模式下表单内容不可编辑，请确认文章内容后进行审核操作。
              </p>
            </div>

            {/* 只用一个 Form */}
            <Form form={checkForm} labelWidth={100}>
              <Form.FormItem label="标题" name="title">
                <Input disabled />
              </Form.FormItem>
              <Form.FormItem label="摘要" name="summary">
                <Input disabled />
              </Form.FormItem>
              <Form.FormItem label="分类" name="category">
                <Input disabled />
              </Form.FormItem>
              <Form.FormItem label="封面图" name="coverImage">
                <CoverImageUploader readOnly />
              </Form.FormItem>
              <Form.FormItem label="标签" name="tags">
                <Input disabled />
              </Form.FormItem>
              <Form.FormItem label="当前状态">
                <Tag theme="primary">待审核</Tag>
              </Form.FormItem>
              <Form.FormItem label="置顶" name="isTop">
                <Switch disabled />
              </Form.FormItem>
              
              {/* 审核意见放在同一个 Form 中 */}
              {/* <Form.FormItem label="审核意见" name="checkRemark">
                <Input placeholder="请输入审核意见（可选）" />
              </Form.FormItem> */}
            </Form>

            {/* 文章内容（只读，在 Form 外部） */}
            <div className="mt-4 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">文章内容</label>
              <RichTextEditor value={content} readOnly />
            </div>

            {/* 审核按钮 */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <Button
                theme="default"
                variant="outline"
                onClick={() => {
                  setCheckVisible(false);
                  setCheckingArticle(null);
                  checkForm.reset();
                }}
              >
                取消
              </Button>
              <Button
                theme="danger"
                icon={<CloseCircleIcon />}
                onClick={() => handleCheckSubmit(2)}
              >
                不通过
              </Button>
              <Button
                theme="success"
                icon={<CheckCircleIcon />}
                onClick={() => handleCheckSubmit(1)}
              >
                通过
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      {/* 预览弹窗 */}
      <Dialog
        header="文章预览"
        visible={previewVisible}
        onClose={() => { setPreviewVisible(false); setPreviewArticle(null); }}
        footer={null}
        width={800}
      >
        {previewArticle ? (
          <div className="p-4">
            <h1 className="text-2xl font-bold mb-4">{previewArticle.title}</h1>
            <div className="flex gap-4 text-gray-500 text-sm mb-4">
              <span>作者：{previewArticle.authorName || '匿名'}</span>
              <span>分类：{previewArticle.category || '-'}</span>
              <span>浏览：{previewArticle.viewCount || 0}</span>
            </div>
            {previewArticle.coverImage && (
              <img
                src={previewArticle.coverImage}
                alt="封面"
                className="w-full h-64 object-cover rounded mb-4"
                onError={(e) => { (e.target as HTMLImageElement).src = 'https://placeholder.pics/svg/800x400/CCCCCC/666666/封面图片加载失败'; }}
              />
            )}
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: previewArticle.content || '' }} />
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">加载中...</div>
        )}
      </Dialog>
    </div>
  );
}
